import { ChangeEvent } from 'react';
import { FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { CoreBox, CoreCheckbox, CoreFormControl, CoreFormControlLabel, CoreTypography } from '@youscience/core';
import {
  Recommendation,
  SchoolReportRequest,
  SchoolReportSchema,
  TranscriptGradeInclusionSR,
  TranscriptNumber,
  YesNo,
} from '@youscience/commonapp-service-common';

import { TabSections } from '@interfaces/recommenderWorkspace';

import { FormState } from '@constants/commonApp/types';
import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';

import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';
import SectionAccordion from '@components/SectionAccordion';

import { StyledFieldsContainer } from '@pages/RecommenderSpace/components/Card.styles';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

import { getFilename } from '../../../../common/formUtils';
import FileUpload from '../../../../common/FileUpload';

export type TranscriptsFields = Partial<SchoolReportRequest[TabSections.Transcripts]>;

interface TranscriptsProps {
  transcripts?: TranscriptsFields;
  onFormChange: (formState: FormState<TranscriptsFields>) => void;
  onExpand: () => void;
  isExtended: boolean;
  isSaved: boolean;
  applicantName?: string;
}

const validator = SchoolReportSchema.shape.transcripts;

export const Transcripts = ({
  transcripts = {},
  onFormChange,
  onExpand,
  isExtended,
  isSaved,
  applicantName = '',
}: TranscriptsProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<TranscriptsFields, typeof validator>(
    transcripts,
    validator,
    isSaved,
    onFormChange,
  );

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<unknown>) => {
    setFormStateWithErrors({ [field]: (event.target.value as string) || '' });
  };

  const handleRadioChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormStateWithErrors({ [field]: event.target.value });
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFormStateWithErrors({ [event.target.name]: checked ? YesNo.Yes : undefined });
  };

  const handleFileChange =
    ({ optional = false } = {}) =>
    (field: string) =>
    async (file: string | undefined) => {
      setFormStateWithErrors({ [field]: file || (optional ? null : undefined) });
    };

  return (
    <SectionAccordion
      title="Transcripts"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={isShowErrors && !formState.isFormValid}
    >
      <StyledFieldsContainer>
        <CoreFormControl fullWidth error={!!formState.errors.numberProvided}>
          <InputLabel required>
            Please indicate number of official transcripts you can provide for the applicant
          </InputLabel>

          <Select
            label="Please indicate number of official transcripts you can provide for the applicant"
            value={formState.data.numberProvided ?? ''}
            onChange={handleSelectChange('numberProvided')}
          >
            {Object.entries(TranscriptNumber).map(([key, value]) => {
              return (
                <MenuItem key={value} value={value}>
                  {key}
                </MenuItem>
              );
            })}
          </Select>

          {formState.errors.numberProvided && <FormHelperText error>{formState.errors.numberProvided}</FormHelperText>}
        </CoreFormControl>

        {(formState.data.numberProvided === TranscriptNumber[1] ||
          formState.data.numberProvided === TranscriptNumber[2] ||
          formState.data.numberProvided === TranscriptNumber[3] ||
          formState.data.numberProvided === TranscriptNumber[4]) && (
          <FileUpload
            title="Upload current or most recent transcript"
            name="currentTranscript"
            initialFile={formState.data.currentTranscript}
            previewTitle="Current Transcript"
            filename={formState.data.currentTranscript ? getFilename(applicantName, 'current_transcript') : undefined}
            onFileUpload={handleFileChange()}
            onFileDelete={handleFileChange()}
            error={formState.errors.currentTranscript}
          />
        )}

        <RadioButtonGroupWithClearButton
          title="Are current courses listed on the transcript?"
          name="currentCoursesListed"
          value={formState.data.currentCoursesListed ?? undefined}
          options={YesNo}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ currentCoursesListed: undefined })}
          error={!!formState.errors.currentCoursesListed}
          helperText={formState.errors.currentCoursesListed}
        />

        {formState.data.currentCoursesListed === YesNo.No && (
          <FileUpload
            title="Please upload a document which lists the current courses for this student"
            name="currentCoursesTranscript"
            initialFile={formState.data.currentCoursesTranscript}
            previewTitle="Current Courses Transcript"
            filename={
              formState.data.currentCoursesTranscript
                ? getFilename(applicantName, 'current_courses_transcript')
                : undefined
            }
            onFileUpload={handleFileChange({ optional: true })}
            onFileDelete={handleFileChange({ optional: true })}
            error={
              formState.errors.currentCoursesTranscript ||
              (!formState.data.currentCoursesTranscript && isSaved ? REQUIRED_FIELD_MESSAGE : '')
            }
          />
        )}

        <RadioButtonGroupWithClearButton
          title="Are college entrance examination scores included with or visible on the transcript?"
          name="testsIncluded"
          value={formState.data.testsIncluded ?? undefined}
          options={YesNo}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ testsIncluded: undefined })}
          error={!!formState.errors.testsIncluded}
          helperText={formState.errors.testsIncluded}
        />

        {(formState.data.numberProvided === TranscriptNumber[2] ||
          formState.data.numberProvided === TranscriptNumber[3] ||
          formState.data.numberProvided === TranscriptNumber[4]) && (
          <FileUpload
            title="Upload additional transcript"
            name="additional2"
            initialFile={formState.data.additional2}
            previewTitle="Additional Transcript 2"
            filename={formState.data.additional2 ? getFilename(applicantName, 'additional_2') : undefined}
            onFileUpload={handleFileChange({ optional: true })}
            onFileDelete={handleFileChange({ optional: true })}
            error={formState.errors.additional2}
          />
        )}

        {(formState.data.numberProvided === TranscriptNumber[3] ||
          formState.data.numberProvided === TranscriptNumber[4]) && (
          <FileUpload
            title="Upload additional transcript"
            name="additional3"
            initialFile={formState.data.additional3}
            previewTitle="Additional Transcript 3"
            filename={formState.data.additional3 ? getFilename(applicantName, 'additional_3') : undefined}
            onFileUpload={handleFileChange({ optional: true })}
            onFileDelete={handleFileChange({ optional: true })}
            error={formState.errors.additional3}
          />
        )}

        {formState.data.numberProvided === TranscriptNumber[4] && (
          <FileUpload
            title="Upload additional transcript"
            name="additional4"
            initialFile={formState.data.additional4}
            previewTitle="Additional Transcript 4"
            filename={formState.data.additional4 ? getFilename(applicantName, 'additional_4') : undefined}
            onFileUpload={handleFileChange({ optional: true })}
            onFileDelete={handleFileChange({ optional: true })}
            error={formState.errors.additional4}
          />
        )}

        <CoreBox sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
          <CoreTypography variant="subtitle2" component="h4">
            Transcript Affirmation
          </CoreTypography>

          <CoreFormControlLabel
            name="affirmation"
            label={
              <CoreTypography>
                I affirm that I have uploaded a transcript to this School Report. I understand that failure to do so
                (by, for example, uploading a blank document or uploading a document promising to mail the transcript at
                a later time) will result in my Common App Online account being closed.
              </CoreTypography>
            }
            control={
              <CoreCheckbox
                value={true}
                onChange={handleCheckboxChange}
                checked={formState.data.affirmation === YesNo.Yes}
              />
            }
          />

          {formState.errors.affirmation && <FormHelperText error>{REQUIRED_FIELD_MESSAGE}</FormHelperText>}
        </CoreBox>

        <CoreFormControl fullWidth error={!!formState.errors.grades}>
          <InputLabel required>
            Please indicate the most recent grades included on the transcript accompanying this form
          </InputLabel>

          <Select
            label="Please indicate the most recent grades included on the transcript accompanying this form"
            value={formState.data.grades ?? ''}
            onChange={handleSelectChange('grades')}
          >
            {Object.entries(TranscriptGradeInclusionSR).map(([key, value]) => {
              return (
                <MenuItem key={value} value={value}>
                  {key}
                </MenuItem>
              );
            })}
          </Select>

          {formState.errors.grades && <FormHelperText error>{formState.errors.grades}</FormHelperText>}
        </CoreFormControl>

        <RadioButtonGroupWithClearButton
          title="I recommend this student"
          name="recommended"
          value={formState.data.recommended ?? undefined}
          options={Recommendation}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ recommended: undefined })}
          error={!!formState.errors.recommended}
          helperText={formState.errors.recommended ? REQUIRED_FIELD_MESSAGE : undefined}
        />
      </StyledFieldsContainer>
    </SectionAccordion>
  );
};
